import axios from 'axios';

export class AuthenticationService {
    private static instance: AuthenticationService;
    private isInitialized = false;

    private constructor() {}

    public static getInstance(): AuthenticationService {
        if (!AuthenticationService.instance) {
            AuthenticationService.instance = new AuthenticationService();
        }
        return AuthenticationService.instance;
    }

    public initialize(): void {
        if (this.isInitialized) {
            return;
        }

        // Set up axios interceptors
        this.setupAxiosInterceptors();
        
        // Load token from localStorage on app start
        this.loadTokenFromStorage();
        this.enforceAdministratorAccessOnLoad();
        
        this.isInitialized = true;
    }

    private setupAxiosInterceptors(): void {
        // Request interceptor to add token to all requests
        axios.interceptors.request.use(
            (config) => {
                const token = this.getToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor to handle 401 responses
        axios.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                if (error.response && error.response.status === 401) {
                    this.handleUnauthorized();
                }
                return Promise.reject(error);
            }
        );
    }

    private loadTokenFromStorage(): void {
        const token = localStorage.getItem('jwtToken');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
    }

    public getToken(): string | null {
        return localStorage.getItem('jwtToken');
    }

    public setToken(token: string): void {
        localStorage.setItem('jwtToken', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    public setUser(user: any): void {
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userId', user.id);
    }

    public getUser(): any {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    }

    public getUserId(): string | null {
        return localStorage.getItem('userId');
    }

    public isLoggedIn(): boolean {
        const token = this.getToken();
        return token !== null && token !== '';
    }

    public isAdministrator(): boolean {
        const user = this.getUser();
        return user?.isAdministrator === true;
    }

    public clearAuthStorage(): void {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        localStorage.removeItem('permissions');
        sessionStorage.removeItem('jwtToken');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('userId');
        delete axios.defaults.headers.common['Authorization'];
    }

    public enforceAdministratorAccessOnLoad(): void {
        if (!this.isLoggedIn()) {
            return;
        }

        if (!this.isAdministrator()) {
            this.denyNonAdministratorAccess();
        }
    }

    public denyNonAdministratorAccess(): void {
        this.clearAuthStorage();
        window.location.href = '/login?error=access';
    }

    public logout(): void {
        this.clearAuthStorage();
        window.location.href = '/login';
    }

    private handleUnauthorized(): void {
        this.clearAuthStorage();

        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/login?error=timeout&redirect=${encodeURIComponent(currentPath)}`;
    }

    public login(user: any): void {
        this.setToken(user.token);
        this.setUser(user);
    }

    // Backward compatibility methods for components that still use sessionStorage
    public getSessionToken(): string | null {
        return sessionStorage.getItem('jwtToken') || this.getToken();
    }

    public getSessionUser(): any {
        const userStr = sessionStorage.getItem('user');
        if (userStr) {
            return JSON.parse(userStr);
        }
        return this.getUser();
    }

    public getSessionUserId(): string | null {
        return this.getUserId() || null;
    }

    public setSessionToken(token: string): void {
        sessionStorage.setItem('jwtToken', token);
        this.setToken(token);
    }

    public setSessionUser(user: any): void {
        sessionStorage.setItem('user', JSON.stringify(user));
        sessionStorage.setItem('userId', user.id);
        this.setUser(user);
    }
} 